import { useState } from 'react';
import { X, FileText, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubmitReportMutation } from '@/store/api/apiSlice';

interface SubmitReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  investigationId: string;
  reportUrl?: string;
}

export default function SubmitReportModal({ isOpen, onClose, investigationId, reportUrl: existingReportUrl }: SubmitReportModalProps) {
  const [investigationNotes, setInvestigationNotes] = useState('');
  const [submitReport, { isLoading }] = useSubmitReportMutation();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!investigationNotes.trim()) {
      setError('Investigation notes are required');
      return;
    }

    if (!investigationId) {
      setError('Investigation ID is missing');
      return;
    }

    try {
      const result = await submitReport({
        id: investigationId,
        data: {
          investigationNotes: investigationNotes.trim(),
          reportUrl: existingReportUrl || `investigation-report-${investigationId}`,
        },
      });

      if ('error' in result) {
        const errData = result.error as any;
        setError(errData?.data?.message || 'Failed to submit report. Please try again.');
      } else {
        setSuccess('Investigation report submitted successfully! Status changed to Under Review.');
        setTimeout(() => {
          setInvestigationNotes('');
          setSuccess('');
          onClose();
        }, 2000);
      }
    } catch (_err) {
      setError('An error occurred. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Center Modal */}
      <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl mx-4 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF6B35]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#FF6B35]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Submit Final Report</h2>
              <p className="text-xs text-gray-500">This will submit the investigation for review</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Info banner */}
          <div className="bg-[#FFF8F4] border border-[#FF6B35]/15 rounded-lg p-3 mb-4">
            <p className="text-xs text-[#4e4d4d] leading-relaxed">
              Your risk assessment and uploaded documents will be included with this submission. The investigation status will change to <span className="font-semibold">Under Review</span>.
            </p>
          </div>

          {/* Investigation Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Investigation Summary <span className="text-red-500">*</span>
            </label>
            <textarea
              value={investigationNotes}
              onChange={(e) => {
                setInvestigationNotes(e.target.value);
                setError('');
              }}
              rows={5}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all resize-none text-sm"
              placeholder="Summarize your investigation findings, observations, and conclusions..."
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Provide a summary of your findings for the super admin to review
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs mt-3">
              {error}
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-xs mt-3 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              {success}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 pt-0">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="flex-1 py-2.5 rounded-lg border-gray-300 hover:bg-gray-100 text-sm"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white py-2.5 rounded-lg font-medium text-sm"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </span>
            ) : (
              'Submit for Review'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
